<template>
  <div class="field-group" :class="state">
    <!-- LABEL -->
    <label v-if="label" class="field-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>

    <!-- TRIGGER -->
    <div
      ref="wrap"
      class="input-wrapper"
      :class="{ disabled, open: isOpen }"
    >
      <button
        type="button"
        class="select-trigger"
        :disabled="disabled"
        @click="toggle"
        @keydown="onTriggerKeydown"
        :aria-expanded="String(isOpen)"
        aria-haspopup="listbox"
      >
        <span class="select-value" :class="{ placeholder: !hasValue }">
          {{ displayLabel }}
        </span>

        <span class="icon">
          <slot name="icon">
            <!-- default arrow -->
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 7.4L0 1.4L1.4 0L6 4.6L10.6 0L12 1.4L6 7.4Z" fill="#6D6F7A"/>
            </svg>
          </slot>
        </span>
      </button>

      <!-- DROPDOWN -->
      <transition name="fade">
        <div
          v-if="isOpen"
          class="dropdown"
          role="listbox"
          :aria-activedescendant="activeId"
        >
          <!-- SEARCH -->
          <div v-if="searchable" class="search-wrap" @mousedown.prevent>
            <input
              ref="searchInput"
              v-model="searchText"
              type="text"
              class="search-input"
              :placeholder="searchPlaceholder"
              @keydown="onSearchKeydown"
            />
          </div>

          <!-- OPTIONS -->
          <template v-if="filteredOptions.length">
            <div
              v-for="(opt, idx) in filteredOptions"
              :key="opt[valueKey]"
              class="option"
              :class="{
                selected: String(opt[valueKey]) === String(modelValue),
                active: idx === activeIndex
              }"
              :id="`opt-${uid}-${idx}`"
              role="option"
              :aria-selected="String(opt[valueKey]) === String(modelValue)"
              @mouseenter="activeIndex = idx"
              @mousedown.prevent="selectOption(opt)"
            >
              <span class="option-label">{{ opt[labelKey] }}</span>

              <svg
                v-if="String(opt[valueKey]) === String(modelValue)"
                class="check"
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20 6L9 17l-5-5" stroke="#1F2937" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </template>

          <!-- EMPTY -->
          <div v-else class="empty">
            Tidak ada hasil
          </div>
        </div>
      </transition>

      <!-- (Optional) hidden select for form semantics -->
      <select
        class="native-hidden"
        :disabled="disabled"
        :value="modelValue"
        @change="handleNativeChange"
      >
        <option v-if="placeholder" disabled value="">{{ placeholder }}</option>
        <option
          v-for="opt in options"
          :key="opt[valueKey]"
          :value="opt[valueKey]"
        >
          {{ opt[labelKey] }}
        </option>
      </select>
    </div>

    <!-- MESSAGE -->
    <div v-if="showMessage" class="field-message">
      <slot name="message-icon" />
      <span>{{ message }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FieldSelect',
  props: {
    modelValue: { type: [String, Number], default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Pilih opsi' },
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: 'id' },
    labelKey: { type: String, default: 'label' },
    state: { type: String, default: 'default' },
    message: { type: String, default: '' },
    disabled: { type: Boolean, default: false },

    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Cari...' },
    // kalau mau search pakai field lain juga, bisa: ['label', 'code']
    searchKey: { type: [String, Array], default: '' }
  },
  data () {
    return {
      isOpen: false,
      activeIndex: -1,
      uid: Math.random().toString(36).slice(2),

      searchText: ''
    }
  },
  computed: {
    showMessage () {
      return !!(this.message && this.state)
    },
    hasValue () {
      return this.modelValue !== '' && this.modelValue !== null && this.modelValue !== undefined
    },
    displayLabel () {
      if (!this.hasValue) return this.placeholder
      const found = this.options.find(o => String(o[this.valueKey]) === String(this.modelValue))
      return found ? found[this.labelKey] : this.placeholder
    },

    // opsi yang tampil setelah difilter
    filteredOptions () {
      if (!this.searchable) return this.options
      const q = (this.searchText || '').trim().toLowerCase()
      if (!q) return this.options

      const keys = Array.isArray(this.searchKey)
        ? this.searchKey
        : (this.searchKey ? [this.searchKey] : [this.labelKey])

      return this.options.filter(opt => {
        return keys.some(k => String(opt?.[k] ?? '').toLowerCase().includes(q))
      })
    },

    activeId () {
      if (this.activeIndex < 0) return ''
      return `opt-${this.uid}-${this.activeIndex}`
    }
  },
  watch: {
    isOpen (val) {
      if (!val) {
        // reset ketika close (boleh dihapus kalau pengen persist)
        this.searchText = ''
        this.activeIndex = -1
        return
      }

      // set active ke selected (di filtered) kalau ada, else first
      const selectedIdx = this.filteredOptions.findIndex(
        o => String(o[this.valueKey]) === String(this.modelValue)
      )
      this.activeIndex = selectedIdx >= 0 ? selectedIdx : (this.filteredOptions.length ? 0 : -1)

      this.$nextTick(() => {
        if (this.searchable && this.$refs.searchInput) {
          this.$refs.searchInput.focus()
          this.$refs.searchInput.select?.()
        }
        this.scrollActiveIntoView()
      })
    },

    // ketika search berubah, aktifnya dibenerin biar ga out of range
    searchText () {
      // kalau hasil kosong
      if (!this.filteredOptions.length) {
        this.activeIndex = -1
        return
      }
      // kalau sebelumnya -1, set ke 0
      if (this.activeIndex < 0) this.activeIndex = 0
      // clamp
      this.activeIndex = Math.min(this.activeIndex, this.filteredOptions.length - 1)
      this.$nextTick(() => this.scrollActiveIntoView())
    },

    // kalau options berubah saat open (misal async load)
    options () {
      if (!this.isOpen) return
      this.$nextTick(() => {
        if (!this.filteredOptions.length) this.activeIndex = -1
        else this.activeIndex = Math.min(Math.max(this.activeIndex, 0), this.filteredOptions.length - 1)
        this.scrollActiveIntoView()
      })
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.onClickOutside)
  },
  beforeUnmount () {
    document.removeEventListener('mousedown', this.onClickOutside)
  },
  methods: {
    toggle () {
      if (this.disabled) return
      this.isOpen = !this.isOpen
    },
    close () {
      this.isOpen = false
      // activeIndex/searchText di-handle watcher isOpen
    },
    onClickOutside (e) {
      const el = this.$refs.wrap
      if (!el) return
      if (!el.contains(e.target)) this.close()
    },
    selectOption (opt) {
      const value = opt[this.valueKey]
      this.$emit('update:modelValue', value)
      this.close()
    },
    handleNativeChange (event) {
      const value = event.target.value
      this.$emit('update:modelValue', value === '' ? '' : value)
    },

    onTriggerKeydown (e) {
      if (this.disabled) return

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.toggle()
        return
      }

      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault()
        this.isOpen = true
        return
      }

      if (!this.isOpen) return

      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
        return
      }

      // Arrow keys ketika focus masih di trigger
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.moveActive(1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveActive(-1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const opt = this.filteredOptions[this.activeIndex]
        if (opt) this.selectOption(opt)
      }
    },

    // keyboard handler khusus search input
    onSearchKeydown (e) {
      if (!this.isOpen) return

      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        // masuk ke list: aktifkan item pertama jika belum ada
        if (this.filteredOptions.length && this.activeIndex < 0) this.activeIndex = 0
        this.scrollActiveIntoView()
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        // arrow up juga tetap navigasi (opsional)
        if (this.filteredOptions.length && this.activeIndex < 0) this.activeIndex = this.filteredOptions.length - 1
        this.scrollActiveIntoView()
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        const opt = this.filteredOptions[this.activeIndex]
        if (opt) this.selectOption(opt)
      }
    },

    // helper move active yang aman
    moveActive (delta) {
      if (!this.filteredOptions.length) return
      if (this.activeIndex < 0) this.activeIndex = 0
      else this.activeIndex = Math.min(
        Math.max(this.activeIndex + delta, 0),
        this.filteredOptions.length - 1
      )
      this.scrollActiveIntoView()
    },

    scrollActiveIntoView () {
      this.$nextTick(() => {
        const dropdown = this.$refs.wrap?.querySelector('.dropdown')
        const active = this.$refs.wrap?.querySelector('.option.active')
        if (!dropdown || !active) return
        const aTop = active.offsetTop
        const aBottom = aTop + active.offsetHeight
        const vTop = dropdown.scrollTop
        const vBottom = vTop + dropdown.clientHeight
        if (aTop < vTop) dropdown.scrollTop = aTop
        else if (aBottom > vBottom) dropdown.scrollTop = aBottom - dropdown.clientHeight
      })
    }
  }
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

/* LABEL */
.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #3c3f44;
}
.required {
  color: #f44336;
  margin-left: 2px;
}

/* WRAPPER */
.input-wrapper {
  position: relative;
  width: 100%;
}

/* TRIGGER */
.select-trigger {
  width: 100%;
  padding: 16px 40px 16px 14px;
  border-radius: 8px;
  border: 1.5px solid #d0d5dd;
  background: #fff;
  font-size: 14px;
  color: #3c3f44;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s, transform .05s;
  text-align: left;
}

.select-trigger:active {
  transform: translateY(0.5px);
}

.select-value.placeholder {
  color: #98a2b3;
}

/* ICON */
.icon {
  position: absolute;
  right: 16px;
  pointer-events: none;
  display: flex;
  align-items: center;
}

/* DROPDOWN */
.dropdown {
  position: absolute;
  z-index: 50;
  left: 0;
  right: 0;
  margin-top: 2px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.12);
  overflow: auto;
  max-height: 240px;
  padding: 6px;
}

/* SEARCH BAR */
.search-wrap {
  position: sticky;
  top: -5.7px;
  background: #fff;
  padding: 8px;
  margin: -6px -6px 6px -6px;
  border-bottom: 1px solid #eef2f6;
  z-index: 2;
}

.search-input {
  width: 100%;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #7b61ff;
  box-shadow: 0 0 0 3px rgba(123, 97, 255, 0.15);
}

/* OPTION */
.option {
  padding: 12px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  transition: background .12s, transform .05s;
  color: #101828;
}

.option:hover,
.option.active {
  background: #f2f4f7;
}

.option.selected {
  background: #eef2ff;
}

.option.selected:hover {
  background: #e6ebff;
}

.check {
  flex-shrink: 0;
}

/* ✅ EMPTY STATE */
.empty {
  padding: 12px;
  color: #667085;
  font-size: 13px;
}

/* STATES */
.field-group.default .select-trigger:focus,
.field-group.default .select-trigger:focus-visible {
  border-color: #7b61ff;
}

.field-group.default .select-trigger:hover {
  border-color: #b6bfcf;
}

/* ERROR */
.field-group.error .select-trigger {
  border-color: #f44336;
}
.field-group.error .field-message {
  color: #f44336;
}

/* WARNING */
.field-group.warning .select-trigger {
  border-color: #f5a544;
}
.field-group.warning .field-message {
  color: #f5a544;
}

/* SUCCESS */
.field-group.success .select-trigger {
  border-color: #2e7d32;
}
.field-group.success .field-message {
  color: #2e7d32;
}

/* DISABLED */
.input-wrapper.disabled .select-trigger {
  background: #f2f4f7;
  border-color: #e4e7ec;
  color: #98a2b3;
  cursor: not-allowed;
}

/* MESSAGE */
.field-message {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* transition */
.fade-enter-active, .fade-leave-active { transition: opacity .12s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* hide native select */
.native-hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
  left: 0;
  top: 0;
}
</style>
